import {
  ExtensionManagerApi,
  GetTechAdaptersRequest,
  GetTechAdaptersResponse,
  GetTechAdapterRequest,
  GetTechAdapterResponse,
  GetTechAdapterDeploymentRequest,
  GetTechAdapterDeploymentResponse,
  UpsertTechAdapterRequest,
  UpsertTechAdapterResponse,
  DeleteTechAdapterRequest,
  UpsertTechAdapterDeploymentRequest,
  UpsertTechAdapterDeploymentResponse,
  DeleteTechAdapterDeploymentRequest,
  GetTechnologiesResponse,
  GetTechnologyRequest,
  GetTechnologyResponse,
  CreateTechnologyRequest,
  CreateTechnologyResponse,
  EditTechnologyRequest,
  EditTechnologyResponse,
  DeleteTechnologyRequest,
} from './api/ExtensionManagerApi';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { handleFailedResponse } from '@agilelab/plugin-wb-platform-common';

export class ExtensionManagerClient implements ExtensionManagerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('extension-manager');
  }

  async getTechnologies(options?: {
    token?: string;
  }): Promise<GetTechnologiesResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/technologies`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async getTechnology(
    request: GetTechnologyRequest,
    options?: { token?: string },
  ): Promise<GetTechnologyResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/technologies/${request.urn}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async createTechnology(
    request: CreateTechnologyRequest,
    options?: { token?: string },
  ): Promise<CreateTechnologyResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/technologies`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
      body: request,
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async editTechnology(
    request: EditTechnologyRequest,
    options?: { token?: string },
  ): Promise<EditTechnologyResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/technologies/${request.id}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
      body: request.body,
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async deleteTechnology(
    request: DeleteTechnologyRequest,
    options?: { token?: string },
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/technologies/${request.urn}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
    });

    await handleFailedResponse(response);
  }

  async getTechAdapters(
    request: GetTechAdaptersRequest,
    options?: { token?: string },
  ): Promise<GetTechAdaptersResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = new URL(`${baseUrl}/tech-adapters`);
    Object.entries(request).forEach(([key, value]) => {
      if (value !== undefined && value !== '')
        url.searchParams.append(key, String(value));
    });

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async getTechAdapter(
    request: GetTechAdapterRequest,
    options?: { token?: string },
  ): Promise<GetTechAdapterResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/tech-adapters/${request.id}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async getTechAdapterDeployment(
    request: GetTechAdapterDeploymentRequest,
    options?: { token?: string },
  ): Promise<GetTechAdapterDeploymentResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/tech-adapters/${request.id}/deployments/${request.environment}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async upsertTechAdapter(
    request: UpsertTechAdapterRequest,
    options?: { token?: string },
  ): Promise<UpsertTechAdapterResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/tech-adapters`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
      body: JSON.stringify(request),
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async deleteTechAdapter(
    request: DeleteTechAdapterRequest,
    options?: { token?: string },
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/tech-adapters/${request.id}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
    });

    await handleFailedResponse(response);
  }

  async upsertTechAdapterDeployment(
    request: UpsertTechAdapterDeploymentRequest,
    options?: { token?: string },
  ): Promise<UpsertTechAdapterDeploymentResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/tech-adapters/${request.id}/deployments/${request.environment}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
      body: JSON.stringify(request),
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async deleteTechAdapterDeployment(
    request: DeleteTechAdapterDeploymentRequest,
    options?: { token?: string },
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/tech-adapters/${request.id}/deployments/${request.environment}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: options?.token ? `Bearer ${options.token}` : '',
      },
    });

    await handleFailedResponse(response);
  }
}
